<template>
  <app-widget 
    :loading="loading"
    :title="$t('device.details')">

    <div slot="widget-header-action">
      <v-btn
        :to="routeConstants.EDIT_DEVICE_URL.replace(':id', singleDevice.id)"
        small
        icon>
        <v-icon>edit</v-icon>
      </v-btn>
    </div>

    <div v-if="!loading" slot="widget-content">
      <v-list>
        <v-list-item :data-cy-device-name="singleDevice.name">
          <v-list-item-content>
            <v-list-item-title>
              {{ $t('device.fields.name') }}: <span>{{ singleDevice.name }}</span>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item :data-cy-device-type="singleDevice.type">
          <v-list-item-content>
            <v-list-item-title>
              {{ $t('device.fields.imei') }}: <span :data-cy-device-imei="singleDevice.imei">{{ singleDevice.imei }}</span>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item>
          <v-list-item-content>
            <v-list-item-title>
              {{ $t('device.fields.deviceType') }}: <span>{{ $t(`device.type.${DEVICE_TYPE_ARR[singleDevice.type]}`) }}</span>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item :data-cy-device-status-type="singleDevice.deviceStatus">
          <v-list-item-content>
            <v-list-item-title>
              {{ $t('device.fields.deviceStatusType') }}: <span>{{ $t(`device.statusType.${DEVICE_STATUS_TYPES_ARR[singleDevice.deviceStatus]}`) }}</span>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item :data-cy-device-refresh-rate="singleDevice.refreshRate">
          <v-list-item-content>
            <v-list-item-title>
              <template v-if="!editRefreshRateFormShow">
                {{ $t('device.fields.refreshRate') }}: 
                {{ singleDevice.refreshRate }}
              </template>

              <template v-if="!editRefreshRateFormShow">
                <v-btn
                  @click="editRefreshRateFormShow = !editRefreshRateFormShow"
                  small
                  icon>
                  <v-icon>edit</v-icon>
                </v-btn>
              </template>
              
              <template v-if="editRefreshRateFormShow">
                <div class="d-inline-block">
                  <v-text-field
                    ref="refreshRateInput"
                    :label="$t('device.fields.refreshRate')"
                    v-model="refreshRateInputValue"
                    :rules="validationRules.refreshRateWithNullChecker"
                  ></v-text-field>
                </div>
                
                <v-btn
                  :loading="loadingNewRefreshRate"
                  color="success"
                  small
                  @click="submitNewRefreshRate"
                  icon>
                  <v-icon>check</v-icon>
                </v-btn>

                <v-btn
                  v-if="!loadingNewRefreshRate"
                  color="error"
                  @click="closeAndResetRefreshRate"
                  small
                  icon>
                  <v-icon>close</v-icon>
                </v-btn>
              </template>
            </v-list-item-title>
            <v-list-item-subtitle v-if="singleDevice.refreshRateChangeStatus !== null">
              <span
                v-if="singleDevice.refreshRateChangeStatus === REFRESH_RATE_CHANGE_STATUS_PARAMS.pending" 
                class="warning--text">

                {{ $t(`device.refreshRateStatuses.${REFRESH_RATE_CHANGE_STATUS[singleDevice.refreshRateChangeStatus]}`) }}
              </span>
              <span 
                v-if="singleDevice.refreshRateChangeStatus === REFRESH_RATE_CHANGE_STATUS_PARAMS.error"
                class="warning--text">
                {{ $t(`device.refreshRateStatuses.${REFRESH_RATE_CHANGE_STATUS[singleDevice.refreshRateChangeStatus]}`) }}
              </span>
              <span 
                v-if="singleDevice.refreshRateChangeStatus === REFRESH_RATE_CHANGE_STATUS_PARAMS.sent"
                class="warning--text">
                {{ $t(`device.refreshRateStatuses.${REFRESH_RATE_CHANGE_STATUS[singleDevice.refreshRateChangeStatus]}`) }}
              </span>
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-list-item :data-cy-sim-phone-number="singleDevice.simPhoneNumber">
          <v-list-item-content>
            <v-list-item-title>
              {{ $t('device.fields.simPhoneNumber') }}: <span>{{ singleDevice.simPhoneNumber || $t('common.notApplicable') }}</span>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item :data-cy-sim-external-device-id="singleDevice.externalDeviceId">
          <v-list-item-content>
            <v-list-item-title>
              {{ $t('device.fields.externalDeviceId') }}: <span>{{ singleDevice.externalDeviceId || $t('common.notApplicable') }}</span>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item :data-cy-battery-percentage="singleDevice.batteryPercentage">
          <v-list-item-content>
            <v-list-item-title v-if="singleDevice.batteryPercentage">
              {{ $t('device.fields.lastKnownBatteryLevel') }}: <span>{{ `${singleDevice.batteryPercentage}%` }}</span>
            </v-list-item-title>

            <v-list-item-title v-else>
              {{ $t('device.fields.lastKnownBatteryLevel') }}: {{ $t('common.notApplicable') }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item :data-cy-creation-date="singleDevice.creationDate">
          <v-list-item-content>
            <v-list-item-title>
              {{ $t('device.fields.creationDate') }}: <span>{{ singleDevice.creationDate | date(FULL_DATE_TIME_FORMAT)}}</span>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item :data-cy-modification-date="singleDevice.modificationDate">
          <v-list-item-content>
            <v-list-item-title>
              <template v-if="!singleDevice.modificationDate">
                {{ $t('device.fields.modificationDate') }}: {{ $t('common.notApplicable') }}
              </template>
              <template v-else>
                {{ $t('device.fields.modificationDate') }}: <span>{{ singleDevice.modificationDate | date(FULL_DATE_TIME_FORMAT)}}</span>
              </template>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </div>
  </app-widget>
</template>

<script>
import { 
  DEVICE_TYPE_ARR,
  DEVICE_STATUS_TYPES_ARR,
  REFRESH_RATE_CHANGE_STATUS,
  REFRESH_RATE_CHANGE_STATUS_PARAMS
} from '@/constants/device.js';

import AppWidget from '@/components/widgets/AppWidget';
import * as routeConstants from '@/router/constants';
import deviceValidationMixin from '@/mixins/deviceValidationRules';
import { FULL_DATE_TIME_FORMAT } from '@/constants/date';
import { NOTIFICATION_ERROR } from '@/common/event.types';

export default {
  name: 'DeviceDetailInfo',
  props: {
    loading: Boolean,
    singleDevice: Object,
    loadingNewRefreshRate: Boolean,
    updateRefreshRateAction: Function
  },
  data: () => ({
    routeConstants,
    DEVICE_TYPE_ARR,
    FULL_DATE_TIME_FORMAT,
    DEVICE_STATUS_TYPES_ARR,
    REFRESH_RATE_CHANGE_STATUS,
    REFRESH_RATE_CHANGE_STATUS_PARAMS,
    editRefreshRateFormShow: false,
    refreshRateInputValue: null
  }),
  methods: {
    closeAndResetRefreshRate () {
      this.refreshRateInputValue = this.singleDevice.refreshRate;
      this.editRefreshRateFormShow = !this.editRefreshRateFormShow;
    },
    submitNewRefreshRate() {
      const fieldIsValid = this.$refs.refreshRateInput.validate();

      if(fieldIsValid) {
        this.updateRefreshRateAction(this.refreshRateInputValue)
          .then(() => {
            this.editRefreshRateFormShow = false;
          })
          .catch(({ data }) => {
            this.editRefreshRateFormShow = false;

            if(typeof data === 'string') {
              this.$eventHub.$emit(NOTIFICATION_ERROR, {
                message: data
              })
            } else {
              this.$eventHub.$emit(NOTIFICATION_ERROR, {
                message: this.$t('common.defaultError')
              })
            }
          })
      }
    }
  },
  watch: {
    singleDevice(newValue) {
      if(newValue) {
        this.refreshRateInputValue = newValue.refreshRate;
      }
    }
  },
  mixins: [deviceValidationMixin],
  components: {
    AppWidget
  }
}
</script>